import React from 'react';

import { ScheduleStatus, TaskStatus } from 'utils/api/modules/tasks/tasks.constants';

import API from 'utils/api';

import { useLocalStorage } from 'utils/hooks/localStorage';

import { FilterType } from 'components/Grid/types';

import { DueDateRange } from '../components/DueDatePicker';
import { TypeFilterValue } from '../components/TypePicker';
import { AssignedToFilterValue } from '../components/AssignedToPicker';
import { MoreFiltersValue } from '../components/MoreFiltersPicker';

import {
  FILTER_FIELDS,
  FilterItem,
  DEFAULT_FILTERS,
  MORE_FILTERS_FIELDS,
  SavedTaskFilters,
  UseTasksFiltersParams,
  UseTasksFiltersReturn,
  getDefaultAssignedTo,
  buildFilterItems,
  arraysEqual,
} from './types';

export const useTasksFilters = ({
  gridApiRef,
  refreshRef,
  user,
  defaultStatus = DEFAULT_FILTERS.status,
  defaultDueDate = DEFAULT_FILTERS.dueDate,
  storageKey,
}: UseTasksFiltersParams): UseTasksFiltersReturn => {
  const defaultSavedFilters: SavedTaskFilters = {
    dueDate: defaultDueDate,
    type: DEFAULT_FILTERS.type,
    status: defaultStatus,
    assignedTo: DEFAULT_FILTERS.assignedTo,
    moreFilters: DEFAULT_FILTERS.moreFilters,
  };

  // Mirrors the Grid component's dual-persistence pattern:
  // useLocalStorage gives immediate restore on mount; DB load overrides with cross-device state.
  const [savedFilters, setSavedFilters] = useLocalStorage<SavedTaskFilters>(storageKey ?? '', defaultSavedFilters);

  // Ref so handlers can read current filters without stale closures or extra deps.
  const savedFiltersRef = React.useRef(savedFilters);
  savedFiltersRef.current = savedFilters;

  const defaultsAppliedRef = React.useRef(false);

  const triggerRefresh = React.useCallback((): void => {
    if (refreshRef.current) {
      refreshRef.current();
    }
  }, [refreshRef]);

  const updateFilterModel = React.useCallback(
    (fieldsToRemove: string | string[], newItems: FilterItem[]): void => {
      if (!gridApiRef.current) return;

      const fieldsArray = Array.isArray(fieldsToRemove) ? fieldsToRemove : [fieldsToRemove];
      const existingItems = gridApiRef.current.state.filter.filterModel.items.filter(
        (item) => !fieldsArray.includes(item.field)
      );

      gridApiRef.current.setFilterModel({
        ...gridApiRef.current.state.filter.filterModel,
        items: [...existingItems, ...newItems],
      });

      triggerRefresh();
    },
    [gridApiRef, triggerRefresh]
  );

  // Load saved preferences from DB, merge with localStorage state
  React.useEffect(() => {
    if (!storageKey) return;
    API.user
      .loadGridPreferences(storageKey)()
      .then((response) => {
        // Nothing saved yet: keep what localStorage restored rather than resetting it to defaults.
        const saved = response.data.state as unknown as Partial<SavedTaskFilters> | null;
        if (!saved) return;
        const merged: SavedTaskFilters = {
          dueDate: saved.dueDate ?? defaultDueDate,
          type: saved.type ?? DEFAULT_FILTERS.type,
          status: saved.status ?? defaultStatus,
          assignedTo: saved.assignedTo ?? DEFAULT_FILTERS.assignedTo,
          moreFilters: saved.moreFilters ?? DEFAULT_FILTERS.moreFilters,
        };
        setSavedFilters(merged);

        if (!gridApiRef.current) return;
        gridApiRef.current.setFilterModel({
          ...gridApiRef.current.state.filter.filterModel,
          items: [
            ...buildFilterItems.status(merged.status),
            ...buildFilterItems.assignedTo(merged.assignedTo),
            ...buildFilterItems.dueDate(merged.dueDate),
            ...buildFilterItems.type(merged.type),
            ...buildFilterItems.moreFilters(merged.moreFilters),
          ],
        });
        triggerRefresh();
      })
      .catch(() => {});
  }, [storageKey]);

  const persist = React.useCallback(
    (filters: SavedTaskFilters): void => {
      if (storageKey) {
        API.user
          .saveGridPreferences(storageKey, filters)()
          .catch(() => {});
      }
    },
    [storageKey]
  );

  const initialAssignedTo =
    savedFiltersRef.current.assignedTo.length > 0 ? savedFiltersRef.current.assignedTo : getDefaultAssignedTo(user);

  const initialFilters = React.useMemo<FilterType[]>(() => {
    const current = savedFiltersRef.current;
    return [
      ...buildFilterItems.status(current.status),
      ...buildFilterItems.assignedTo(initialAssignedTo),
      ...buildFilterItems.dueDate(current.dueDate),
      ...buildFilterItems.type(current.type),
      ...buildFilterItems.moreFilters(current.moreFilters),
    ] as unknown as FilterType[];
  }, []);

  // The grid reads initialFilters directly; this only keeps the pickers in step with the
  // current-user fallback.
  React.useEffect(() => {
    if (defaultsAppliedRef.current || !user) return;
    defaultsAppliedRef.current = true;

    if (initialAssignedTo !== savedFiltersRef.current.assignedTo) {
      setSavedFilters((prev) => ({ ...prev, assignedTo: initialAssignedTo }));
    }
  }, [user, setSavedFilters]);

  const handleDueDateChange = React.useCallback(
    (value: DueDateRange): void => {
      const newFilters = { ...savedFiltersRef.current, dueDate: value };
      setSavedFilters(newFilters);
      updateFilterModel(FILTER_FIELDS.DUE_DATE, buildFilterItems.dueDate(value));
      persist(newFilters);
    },
    [updateFilterModel, persist, setSavedFilters]
  );

  const handleTypeChange = React.useCallback(
    (value: TypeFilterValue): void => {
      const newFilters = { ...savedFiltersRef.current, type: value };
      setSavedFilters(newFilters);
      updateFilterModel(FILTER_FIELDS.TYPE, buildFilterItems.type(value));
      persist(newFilters);
    },
    [updateFilterModel, persist, setSavedFilters]
  );

  const handleStatusChange = React.useCallback(
    (value: (TaskStatus | ScheduleStatus)[]): void => {
      const newFilters = { ...savedFiltersRef.current, status: value };
      setSavedFilters(newFilters);
      updateFilterModel(FILTER_FIELDS.STATUS, buildFilterItems.status(value));
      persist(newFilters);
    },
    [updateFilterModel, persist, setSavedFilters]
  );

  const handleAssignedToChange = React.useCallback(
    (value: AssignedToFilterValue[]): void => {
      const newFilters = { ...savedFiltersRef.current, assignedTo: value };
      setSavedFilters(newFilters);
      updateFilterModel(FILTER_FIELDS.ASSIGNED_TO, buildFilterItems.assignedTo(value));
      persist(newFilters);
    },
    [updateFilterModel, persist, setSavedFilters]
  );

  const handleMoreFiltersChange = React.useCallback(
    (value: MoreFiltersValue): void => {
      const newFilters = { ...savedFiltersRef.current, moreFilters: value };
      setSavedFilters(newFilters);
      updateFilterModel(MORE_FILTERS_FIELDS, buildFilterItems.moreFilters(value));
      persist(newFilters);
    },
    [updateFilterModel, persist, setSavedFilters]
  );

  const hasActiveFilters = React.useMemo((): boolean => {
    const isDueDateActive =
      savedFilters.dueDate.from !== defaultDueDate.from || savedFilters.dueDate.to !== defaultDueDate.to;
    const isTypeActive = savedFilters.type !== DEFAULT_FILTERS.type;
    const isMoreFiltersActive =
      savedFilters.moreFilters.site !== null ||
      savedFilters.moreFilters.asset !== null ||
      savedFilters.moreFilters.contact !== null ||
      savedFilters.moreFilters.startDate.from !== null ||
      savedFilters.moreFilters.startDate.to !== null;
    const isStatusDefault = arraysEqual(savedFilters.status, defaultStatus);
    const isAssignedToDefault =
      user && savedFilters.assignedTo.length === 1 && savedFilters.assignedTo[0].uuid === user.uuid;

    return isDueDateActive || isTypeActive || isMoreFiltersActive || !isStatusDefault || !isAssignedToDefault;
  }, [savedFilters, user, defaultDueDate, defaultStatus]);

  const handleResetFilters = React.useCallback((): void => {
    const defaultAssignedTo = getDefaultAssignedTo(user);
    const resetFilters: SavedTaskFilters = {
      dueDate: defaultDueDate,
      type: DEFAULT_FILTERS.type,
      status: defaultStatus,
      assignedTo: defaultAssignedTo,
      moreFilters: DEFAULT_FILTERS.moreFilters,
    };
    setSavedFilters(resetFilters);
    persist(resetFilters);

    if (!gridApiRef.current) return;

    gridApiRef.current.setFilterModel({
      ...gridApiRef.current.state.filter.filterModel,
      items: [
        ...buildFilterItems.status(defaultStatus),
        ...buildFilterItems.assignedTo(defaultAssignedTo),
        ...buildFilterItems.dueDate(defaultDueDate),
      ],
    });

    triggerRefresh();
  }, [user, gridApiRef, triggerRefresh, persist, setSavedFilters, defaultDueDate, defaultStatus]);

  return {
    initialFilters,
    dueDateFilter: savedFilters.dueDate,
    typeFilter: savedFilters.type,
    statusFilter: savedFilters.status,
    assignedToFilter: savedFilters.assignedTo,
    moreFilters: savedFilters.moreFilters,
    hasActiveFilters,
    handleDueDateChange,
    handleTypeChange,
    handleStatusChange,
    handleAssignedToChange,
    handleMoreFiltersChange,
    handleResetFilters,
  };
};
